/**
 * We show the list of delivery issues for a given dasher here by
 * making a network request for this dasher's list.
 *
 * Each delivery issue will be tappable and lead the user to the detail
 * view for that particular issue.
 */

import { noop } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import NavBar from '@doordash/component-navbar'
import Separator from '@doordash/component-separator'
import {
  Text,
  Colors,
  Inset,
  StackChildren,
  Stack,
} from '@doordash/design-language'

import * as actions from '../../../../actions'
import {
  DeliveryIssueListItem,
  DeliveryIssueStatus,
  DeliveryIssueTypes,
  PolicyViolationPages,
} from '../../../../models/types'
import routes from '../../../../routes'
import {
  deliveryIssuesSelector,
  getDasherId,
  isLoadingDeliveryIssuesList,
} from '../../../../selectors/deliveryIssues'
import { ANDROID_CLOSE_DEEPLINK, IOS_CLOSE_DEEPLINK } from '../constants'
import ListCellMenuItem from '../components/ListCellMenuItem'
import { StyledLoading } from '../loading'
import { formatTimestamp, goToDeepLink } from '../utilities'
import t from './strings'
import LearnMore from '../components/LearnMore'
import { NavBarHeaderTitle, StickyTop } from '../components/styles'
import * as events from '../../../../events'
import { PolicyViolation, EventActions } from '../../../../typeModules/events'

type DeliveryIssueListItemProps = {
  issueId: string
  status: DeliveryIssueStatus
  subtitle: string
  title: string
  onClick?: () => void
}

const DeliveryIssueListItem: React.FC<DeliveryIssueListItemProps> = ({
  title,
  issueId,
  status,
  subtitle,
  onClick,
}) => {
  return (
    <Link
      to={routes.deliveryIssueDetail.buildPath({ id: issueId })}
      style={{ textDecoration: 'none' }}
    >
      <ListCellMenuItem
        title={title}
        status={status}
        subtitle={subtitle}
        onClick={onClick}
      />
    </Link>
  )
}

type DeliveryIssuesListProps = {
  dasherId: string
  deliveryIssuesList: DeliveryIssueListItem[]
  getDeliveryIssuesList: (dasherId: string) => void
  isLoading: boolean
  track: (data: PolicyViolation) => Promise<any>
}

export const DeliveryIssuesList: React.FC<DeliveryIssuesListProps> = ({
  dasherId,
  deliveryIssuesList,
  getDeliveryIssuesList,
  isLoading,
  track,
}) => {
  React.useEffect(() => {
    track({
      page: PolicyViolationPages.ViolationList,
      action: EventActions.Rendered,
    })
  }, [])
  React.useEffect(() => {
    // Fetch delivery issues for the dasher on mount after dasherId exists
    if (dasherId !== null) {
      getDeliveryIssuesList(dasherId)
    }
  }, [dasherId])

  return (
    <div>
      <StickyTop>
        <NavBar
          onNavigationClick={() =>
            goToDeepLink({
              ios: IOS_CLOSE_DEEPLINK,
              android: ANDROID_CLOSE_DEEPLINK,
            })
          }
          onOverflowClick={noop}
          textAlign={NavBar.TextAlignments.Center}
          isSeparatorVisible={false}
        />
      </StickyTop>
      <NavBarHeaderTitle>
        <Text styles={Text.Styles.MajorPageTitle}>
          {t.contractViolationTitle()}
        </Text>
      </NavBarHeaderTitle>
      {/* render loading indicator or issues list */}
      {isLoading ? (
        <StyledLoading />
      ) : (
        <>
          <Inset isSquished={true}>
            <StackChildren>
              <Text color={Colors.SystemGrey60}>
                {t.contractViolationDescription()}
              </Text>
              <Stack>
                <LearnMore
                  onClick={() =>
                    track({
                      page: PolicyViolationPages.ViolationList,
                      action: EventActions.Clicked,
                      data: {
                        name: 'learn_more',
                      },
                    })
                  }
                />
              </Stack>
            </StackChildren>
          </Inset>
          <Separator />
          <Inset>
            <Text style={Text.Styles.SectionSubtext}>
              {t.contractViolationSubDescription()}
            </Text>
          </Inset>
          <Separator type={Separator.Types.FullBorder} />
          <div role="list">
            {deliveryIssuesList.map(issue => {
              return (
                <DeliveryIssueListItem
                  key={issue.id}
                  title={
                    issue.issueType === DeliveryIssueTypes.ExtremelyLate
                      ? t.contractViolationExtremelyLateTitle()
                      : t.contractViolationNeverArrivedTitle()
                  }
                  status={issue.status}
                  subtitle={formatTimestamp(issue.deliveryCreatedAt)}
                  issueId={issue.id}
                  onClick={() =>
                    track({
                      type: issue.issueType,
                      page: PolicyViolationPages.ViolationList,
                      action: EventActions.Clicked,
                      data: {
                        name: 'violation',
                        issueId: issue.id,
                      },
                    })
                  }
                />
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  deliveryIssuesList: deliveryIssuesSelector(state),
  dasherId: getDasherId(state),
  isLoading: isLoadingDeliveryIssuesList(state),
})

const mapDispatchToProps = {
  getDeliveryIssuesList: actions.getDeliveryIssuesList,
  track: events.trackPolicyViolations,
}

export const DisconnectedDeliveryIssuesList = DeliveryIssuesList
export default connect(mapStateToProps, mapDispatchToProps)(DeliveryIssuesList)
